import { useEffect, useState, useMemo, useRef, useCallback } from "react"
import { fboApi } from "@/api/fbo-api.client"
import type { GetToken } from "@/api/ats-sync-api.client"
import type { UserProfileWorkerDto } from "@/api/fbo-api.types"
import type { WorkerProfile, ContactStatus, ProfileStatus } from "@/components/ats-fbo/types"
import { USER_STATUS, PROFILE_STATUS, cultureIdToCode } from "@/components/ats-fbo/constants"
import { useCodeValues } from "./use-code-values"
import { CODE_VALUE_GROUPS } from "@/types/code-values"
import { env } from "@/lib/env"
import { logger } from "@/lib/logger"
import { workerProfiles as mockWorkerProfiles, mockCreatedEntities } from "@/components/ats-fbo/mock-data"

/** Map numeric UserStatusId to a simplified UI-level ContactStatus */
export function mapContactStatus(userStatusId: number): ContactStatus {
  switch (userStatusId) {
    case USER_STATUS.Active: return "Active"
    case USER_STATUS.Inactive: return "Inactive"
    case USER_STATUS.Draft:
    case USER_STATUS.PendingReview:
    case USER_STATUS.Declined:
    case USER_STATUS.Recalled:
      return "PendingApproval"
    case USER_STATUS.PendingChange: return "PendingChange"
    case USER_STATUS.Terminated: return "Terminated"
    default: return "Active"
  }
}

/** Map numeric ProfileStatusId to a simplified UI-level ProfileStatus */
export function mapProfileStatus(profileStatusId: number): ProfileStatus {
  switch (profileStatusId) {
    case PROFILE_STATUS.Active: return "Active"
    case PROFILE_STATUS.Inactive: return "Inactive"
    case PROFILE_STATUS.Draft:
    case PROFILE_STATUS.PendingReview:
    case PROFILE_STATUS.Declined:
    case PROFILE_STATUS.Recalled:
      return "PendingApproval"
    case PROFILE_STATUS.PendingChange: return "PendingChange"
    case PROFILE_STATUS.Terminated: return "Terminated"
    default: return "Active"
  }
}

/** Convert an API DTO to the v3 WorkerProfile UI type */
function dtoToWorkerProfile(
  p: UserProfileWorkerDto,
  getCodeValueCode: (id: number, group: string) => string,
): WorkerProfile {
  return {
    id: String(p.Id),
    contactId: String(p.ContactId),
    name: p.Contact?.FullName ?? "Unknown",
    email: p.PrimaryEmail ?? "",
    profileTypeCode: getCodeValueCode(p.ProfileTypeId, CODE_VALUE_GROUPS.PROFILE_TYPE),
    workerOrgId: p.OrganizationId != null ? String(p.OrganizationId) : null,
    contactStatus: mapContactStatus(p.Contact?.UserStatusId ?? 1),
    profileStatus: mapProfileStatus(p.ProfileStatusId),
    // Surface the contact-level CultureId mapped to a culture code. Null is preserved
    // (instead of defaulting to English here) so the consumer can distinguish "FBO
    // has no value" from "FBO explicitly says English" and fall through to the
    // PreOnboard value when appropriate.
    preferredLanguage: cultureIdToCode(p.Contact?.CultureId),
  }
}

export interface WorkerProfilesResult {
  allProfiles: WorkerProfile[]
  loading: boolean
}

/**
 * Fetches all profiles for the given contact IDs.
 * Used to populate the contact-level dropdown in the drawer.
 *
 * @param contactIds   Resolved contact IDs from the landscape hook.
 *                     Pass empty array when no contacts are resolved (hook is inert).
 * @param refreshKey   Increment to force a re-fetch even when contactIds haven't changed.
 *                     Used after applying conflict corrections to pick up profiles created
 *                     by other users since the last fetch.
 */
export function useWorkerProfiles(
  contactIds: string[],
  getToken: GetToken,
  refreshKey: number = 0,
): WorkerProfilesResult {
  const useMock = env.useMockData || !env.fboApiUrl
  const { getCodeValueCode } = useCodeValues()
  const [apiProfiles, setApiProfiles] = useState<UserProfileWorkerDto[]>([])
  const [loading, setLoading] = useState(false)

  // Stable serialized key for the contactIds array to avoid re-fetching on referential changes
  const contactIdsKey = contactIds.join(",")
  const prevKeyRef = useRef(contactIdsKey)
  const prevRefreshKeyRef = useRef(refreshKey)

  useEffect(() => {
    if (useMock || contactIds.length === 0) {
      setApiProfiles([])
      if (prevKeyRef.current !== contactIdsKey) {
        prevKeyRef.current = contactIdsKey
      }
      return
    }

    const refreshKeyChanged = prevRefreshKeyRef.current !== refreshKey
    prevRefreshKeyRef.current = refreshKey

    // Only refetch if the IDs changed or a refresh was explicitly requested (refreshKey bumped)
    if (prevKeyRef.current === contactIdsKey && apiProfiles.length > 0 && !refreshKeyChanged) return
    prevKeyRef.current = contactIdsKey

    let cancelled = false
    setLoading(true)

    // TODO: check for perforamnce
    Promise.all(
      contactIds.map((id) =>
        fboApi.searchWorkerProfilesByContact(Number(id), getToken)
      )
    )
      .then((results) => {
        if (!cancelled) setApiProfiles(results.flat())
      })
      .catch((err: unknown) => {
        logger.error(err)
        if (!cancelled) setApiProfiles([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [contactIdsKey, getToken, useMock, refreshKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // Convert API DTOs to WorkerProfile[]
  const allApiProfiles = useMemo(
    () => apiProfiles.map((p) => dtoToWorkerProfile(p, getCodeValueCode)),
    [apiProfiles, getCodeValueCode]
  )

  // Mock: filter by contactIds (include dynamically created entities from correction flow)
  const allMockProfiles = useMemo(
    () => {
      if (contactIds.length === 0) return []
      const all = [...mockWorkerProfiles, ...mockCreatedEntities.profiles]
      return all.filter((w) => contactIds.includes(w.contactId))
    },
    [contactIdsKey, refreshKey] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const allProfiles: WorkerProfile[] = useMock ? allMockProfiles : allApiProfiles

  return { allProfiles, loading }
}

/** Returns a stable callback for fetching a single worker profile by ID */
export function useWorkerProfileLookup(getToken: GetToken) {
  const useMock = env.useMockData || !env.fboApiUrl
  return useCallback(
    (profileId: number) => {
      if (useMock) {
        const profile = mockWorkerProfiles.find((p) => p.id === String(profileId))
          ?? mockCreatedEntities.profiles.find((p) => p.id === String(profileId))
        if (!profile) return Promise.resolve(null)
        // Return a shape compatible with UserProfileWorkerDto so computeCorrections
        // can read Id and ContactId. Use `as any` because mock IDs are strings.
        return Promise.resolve({ Id: profileId, ContactId: profile.contactId as any })
      }
      return fboApi.getWorkerProfileById(profileId, getToken)
    },
    [getToken, useMock],
  )
}
